import { CommonModule } from "@angular/common";
import { Component, inject } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { FormBuilder, ReactiveFormsModule, Validators } from "@angular/forms";
import { Router, RouterModule } from "@angular/router";

import { GoogleSigninButtonModule } from "@abacritt/angularx-social-login";
import { AlertComponent } from "@common/alert/alert.component";
import { InputPasswordComponent } from "@common/form/input-password/input-password.component";
import { InputTextComponent } from "@common/form/input-text/input-text.component";
import { AuthLayoutComponent } from "@common/layout/auth-layout/auth-layout.component";
import { LoginFormError } from "@models/form";
import { TranslateModule } from "@ngx-translate/core";
import { AuthService } from "@services/auth.service";
import { LocalStorageService } from "@services/local-storage.service";
import { NetworkStatusService } from "@services/network-status.service";
import { TranslationService } from "@services/translation.service";
import { ButtonModule } from "primeng/button";
import { DialogModule } from "primeng/dialog";
import { DividerModule } from "primeng/divider";

@Component({
  selector: "app-login",
  standalone: true,
  imports: [
    CommonModule,
    AuthLayoutComponent,
    DividerModule,
    ReactiveFormsModule,
    ButtonModule,
    InputTextComponent,
    InputPasswordComponent,
    TranslateModule,
    GoogleSigninButtonModule,
    RouterModule,
    AlertComponent,
    DialogModule,
  ],
  templateUrl: "./login.component.html",
})
export class LoginComponent {
  private readonly routerService = inject(Router);
  private readonly authservice = inject(AuthService);
  private readonly formBuilder = inject(FormBuilder);
  private readonly translationService = inject(TranslationService);
  private readonly localStorageService = inject(LocalStorageService);
  private errorMessage: LoginFormError = {};

  readonly networkService = inject(NetworkStatusService);

  loginForm = this.formBuilder.group({
    email: [
      "",
      [
        Validators.required,
        Validators.pattern(
          "[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?"
        ),
      ],
    ],
    password: ["", [Validators.required]],
  });
  loading = false;
  errorAlertMessage = "";

  constructor() {
    this.translationService
      .get("forms")
      .pipe(takeUntilDestroyed())
      .subscribe(val => {
        this.errorMessage["wrong-credentials"] = val["login"]["wrong-credentials"];
        this.errorMessage["required"] = val["login"]["required"];
        this.errorMessage["notValidEmail"] = val["notValidEmail"];
        this.errorMessage["only-enterprise-user"] = val["login"]["only-enterprise-user"];
      });
  }

  getErrorMessage(field: "email" | "password") {
    if (this.loginForm.get(field)?.errors?.["required"]) {
      return this.errorMessage.required?.[field] ?? "";
    }
    if (this.loginForm.get(field)?.errors?.["pattern"]) {
      return this.errorMessage.notValidEmail ?? "";
    }
    return "";
  }

  async login() {
    this.loginForm.markAllAsTouched();
    if (this.loginForm.valid) {
      try {
        this.loading = true;
        await this.authservice.login({
          email: this.loginForm.get("email")?.value ?? "",
          password: this.loginForm.get("password")?.value ?? "",
        });
        this.loading = false;
      } catch (error: any) {
        this.loading = false;
        if (error.message === "Error: not allow")
          this.errorAlertMessage = "wrong-credentials" ?? "";
        else if (
          error.message === "Error: The user is pending verification by the admin." ||
          error.message === "Error: The user is blocked."
        )
          this.routerService.navigate(["soon"]);
        else if (error.message === "Error: Individual user") {
          this.errorAlertMessage = "only-enterprise-user" ?? "";
        } else this.errorAlertMessage = "wrong-credentials" ?? "";
      }
    }
  }
}
